import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import StatusBadge from '../StatusBadge';
import AppStyles from '../../AppStyles';
import { formatRelativeTime } from '../../util/format';
import { useTranslation } from '../../i18n';

/**
 * ReportSummaryRow - Compact at-a-glance strip shown directly under the report title.
 *
 * Read-only echo of status, hazard type, and location so a reader can triage the
 * report without scrolling past the description and image gallery. The editable
 * Tags section further down remains the place to change these values.
 *
 * Shared between ReportDetails.js and AdminReportDetails.js
 *
 * @param {Object} props
 * @param {string} [props.status] - Ticket status: 'Open' | 'InProgress' | 'Closed'
 * @param {string[]} [props.kpis] - Hazard type tags
 * @param {string[]} [props.locations] - Location tags
 * @param {string} [props.assignedTo] - Assigned analyst email (admin view)
 * @param {number} [props.lastUpdated] - Timestamp of most recent activity
 * @param {number} [props.commentCount] - Number of comments on the report
 */
const ReportSummaryRow = ({
  status,
  kpis = [],
  locations = [],
  assignedTo,
  lastUpdated,
  commentCount,
}) => {
  const { t } = useTranslation('reports');

  const hasChips = kpis.length > 0 || locations.length > 0 || assignedTo;
  const hasActivity = lastUpdated || commentCount > 0;

  if (!status && !hasChips && !hasActivity) {
    return null;
  }

  const activityParts = [];
  if (lastUpdated) {
    // Whole-sentence key rather than "Updated" + a formatted time: the relative
    // time still comes from the shared helper (already localized), but each
    // language owns its own word order and capitalization.
    activityParts.push(
      t('summary.updatedAgo', {
        time: formatRelativeTime(lastUpdated, { midSentence: true }),
      }),
    );
  }
  if (commentCount > 0) {
    activityParts.push(t('summary.commentCount', { count: commentCount }));
  }

  return (
    <View style={styles.container}>
      <View style={styles.chipRow}>
        {status && <StatusBadge status={status} compact />}

        {kpis.map((kpi) => (
          <View key={`summary-kpi-${kpi}`} style={styles.chip}>
            <Feather name="tag" size={11} color={AppStyles.colorSet.textSecondary} />
            <CustomText font="medium" size={11} color={AppStyles.colorSet.textSecondary}>
              {kpi}
            </CustomText>
          </View>
        ))}

        {locations.map((location) => (
          <View key={`summary-loc-${location}`} style={styles.chip}>
            <Feather name="map-pin" size={11} color={AppStyles.colorSet.textSecondary} />
            <CustomText font="medium" size={11} color={AppStyles.colorSet.textSecondary}>
              {location}
            </CustomText>
          </View>
        ))}

        {assignedTo && (
          <View style={styles.chip}>
            <Feather name="user" size={11} color={AppStyles.colorSet.textSecondary} />
            <CustomText font="medium" size={11} color={AppStyles.colorSet.textSecondary}>
              {assignedTo}
            </CustomText>
          </View>
        )}
      </View>

      {activityParts.length > 0 && (
        <CustomText
          style={styles.activityLine}
          font="regular"
          size={12}
          color={AppStyles.colorSet.textMuted}
        >
          {activityParts.join(' · ')}
        </CustomText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 8,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: AppStyles.borderRadii.pill,
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
  },
  activityLine: {
    marginTop: 6,
    paddingHorizontal: 2,
  },
});

export default ReportSummaryRow;
